// Libraries
import { useEffect, useState } from 'react';
import { Buffer } from 'buffer';
import { RootState } from '@redux/rootReducer';
import { useAppDispatch, useAppSelector } from './redux';
import { approvePendingDocuments, getAllPendingDocumentsToBeApprove } from '@redux/onboarding/onboarding.slice';
// Hooks
import { usePrintPdf } from './usePrintPdf';
// Models
import { IPendingDocuments } from '@models/Onboarding';
// Constants
import { DOCUMENT_DOWNLOAD_NAMES } from '@constants/Onboarding';

export const useOnboardingActions = (): {
    pendingDocuments: IPendingDocuments[];
    approveDocuments: (
        userId: string,
        documentTypeId: string,
        documentNumber: string,
        documentNames: string[],
        documentProducts: string[],
        refreshDocuments: boolean
    ) => Promise<boolean>;
    openDocumentPdf: (documentName: string, documentProduct: string) => void;
    getDocumentInformation: (documentName: string, documentProduct: string) => IPendingDocuments | null;
    validateDocumentExistance: (documentName: string, documentProduct: string) => boolean;
} => {
    const { downloadBase64 } = usePrintPdf();
    const {
        onboarding: { dataProcessing },
    } = useAppSelector((state: RootState) => state);
    const dispatch = useAppDispatch();
    const [pendingDocuments, setPendingDocuments] = useState<IPendingDocuments[]>([]);

    useEffect(() => {
        if (dataProcessing.length > 0) setPendingDocuments(dataProcessing);
    }, [dataProcessing]);

    const openDocumentPdf = (documentName: string, documentProduct: string): void => {
        const document = dataProcessing.find(
            document => document.documentName == documentName && document.product == documentProduct
        );
        if (document) {
            const downloadDocumentName = DOCUMENT_DOWNLOAD_NAMES[documentName];
            const convertBase64 = Buffer.from(document.pdf.data).toString('base64');
            downloadBase64(convertBase64, downloadDocumentName);
        }
    };

    const getDocumentInformation = (documentName: string, documentProduct: string): IPendingDocuments | null => {
        if (dataProcessing.length > 0) {
            const document = dataProcessing.find(
                document => document.documentName == documentName && document.product == documentProduct
            );
            if (document) return document;
            return null;
        }
        return null;
    };

    const validateDocumentExistance = (documentName: string, documentProduct: string): boolean => {
        if (dataProcessing.length > 0) {
            const document = dataProcessing.some(
                document => document.documentName == documentName && document.product == documentProduct
            );
            return document;
        }
        return false;
    };

    const approveDocuments = async (
        userId: string,
        documentType: string,
        documentNumber: string,
        documentNames: string[],
        documentProducts: string[],
        refreshDocuments: boolean
    ): Promise<boolean> => {
        const documentsInList = dataProcessing.filter(
            document => documentNames.includes(document.documentName) && documentProducts.includes(document.product)
        );
        const documentsFormated = documentsInList.map(document => ({
            version: document.version,
            documentName: document.documentName,
            product: document.product,
        }));
        const ApproveDocumentParams = {
            userId: userId,
            documentType: documentType,
            documentNumber: documentNumber,
            documents: documentsFormated,
        };
        const approveResponse = await dispatch(approvePendingDocuments(ApproveDocumentParams));
        if (refreshDocuments && approveResponse) await dispatch(getAllPendingDocumentsToBeApprove(documentType, documentNumber));
        return approveResponse;
    };

    return {
        pendingDocuments,
        approveDocuments,
        openDocumentPdf,
        getDocumentInformation,
        validateDocumentExistance,
    };
};
